import React, { useMemo, useState } from "react";
import Select from "react-select";
import { NumericFormat } from "react-number-format";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import FormLabel from "../../FormLabel";
import clsx from "clsx";
import ErrorSelect from "../../ErrorSelect";
import ItemTaxPicker from "../Accounting/ItemTaxPicker";
import { currencyFormat } from "../../../Utils";
import { calculateItemTaxBreakdown } from "../../../Utils/AccountingUtil";

const ProductEntryTable = ({
  productItems,
  setProductItems,
  products,
  productLoading,
  updateProductItem,
  handleRemoveRow,
  handleAddRow,
  productItemsError,
  businessProductsError,
  taxes,
  taxesLoading,
  taxesError,
  onRefresh,
  maxProducts = 5,
  isPurchase = false,
  // The document context the tax depends on. Without it a line would tax its
  // gross amount while the summary below taxes the discounted one.
  subtotal = 0,
  totalDiscount = 0,
  applyAfterTax = false,
  // The business's whole tax list, for names only. `taxes` above is the
  // picker's list and is filtered by direction, so a line carrying a tax the
  // picker hides — or one picked before the list loaded — would read
  // "Pajak tidak tersedia" here while the summary below named it correctly.
  taxDirectory = null,
  // WHOLE_RUPIAH for a new invoice or one saved under it; see
  // calculateItemTaxBreakdown.
  taxRounding = null,
}) => {
  const { t } = useTranslation();
  const [collapsedItems, setCollapsedItems] = useState([]);

  // A bill is priced at what the business pays, a sales invoice at what it
  // charges. Normalising the option's own `price` — rather than reading cost at
  // each use — keeps every downstream fallback on the right number:
  // resolveItemPrice, getAllTaxes and transformInvoiceItems in AccountingUtil,
  // and InvoiceModal's price validation.
  const productOptions = useMemo(
    () =>
      products.slice().map((obj) => {
        const unitPrice = isPurchase ? obj.cost : obj.price;
        // A product with nothing on file shows its name alone rather than Rp0.
        const priceLabel =
          unitPrice > 0 ? ` - ${currencyFormat(unitPrice)}` : "";
        return {
          ...obj,
          value: obj.id,
          price: unitPrice,
          label: `${obj.name}${priceLabel}`,
        };
      }),
    [products, isPurchase],
  );

  const isMaxProductsReached = productItems.length >= maxProducts;

  // Create a modified handleAddRow function that will also collapse previous row
  const handleAddRowWithCollapse = () => {
    // Don't add if we've reached the maximum
    if (isMaxProductsReached) return;

    // Collapse the last product (previous last row)
    if (productItems.length > 0) {
      const lastIndex = productItems.length - 1;
      if (!collapsedItems.includes(lastIndex)) {
        setCollapsedItems((prev) => [...prev, lastIndex]);
      }
    }

    // Call original handleAddRow
    handleAddRow();
  };

  const toggleCollapse = (index) => {
    setCollapsedItems((prevState) => {
      if (prevState.includes(index)) {
        return prevState.filter((item) => item !== index);
      } else {
        return [...prevState, index];
      }
    });
  };

  // This is our fixed version of the remove row handler that also updates collapsed state
  const handleRemoveRowWithCollapseFix = (index) => {
    // First remove the row through the original handler
    handleRemoveRow(index);

    // Then update our collapsed items state
    setCollapsedItems((prevState) => {
      // Remove the collapsed state for the deleted item
      const newState = prevState.filter((item) => item !== index);

      // Shift down indexes of items that come after the deleted item
      return newState.map((item) => {
        if (item > index) {
          return item - 1;
        }
        return item;
      });
    });
  };

  const isCollapsed = (index) => collapsedItems.includes(index);

  return (
    <div className="flex flex-col w-full">
      <div className="space-y-4">
        {productItems?.map((item, index) => {
          const {
            quantity,
            taxBaseRate,
            otherTaxBase: taxBase,
            taxes: itemTaxes,
            totalTax: itemTax,
            itemTotal,
          } = calculateItemTaxBreakdown(
            item,
            subtotal,
            totalDiscount,
            applyAfterTax,
            taxDirectory,
            taxRounding,
          );
          return (
            <div
              key={index}
              className={clsx(
                "border border-gray-200 rounded-lg p-4 bg-white shadow-sm",
                productItemsError?.[index] && "border-red-500",
              )}
            >
              <div className="flex justify-between">
                <div className="flex-grow">
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => toggleCollapse(index)}
                      className="mr-2 text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                      aria-expanded={!isCollapsed(index)}
                      aria-label={
                        isCollapsed(index)
                          ? "Expand product"
                          : "Collapse product"
                      }
                    >
                      {isCollapsed(index) ? (
                        <ChevronRightIcon className="h-5 w-5" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5" />
                      )}
                    </button>
                    <h3 className="text-sm font-medium text-gray-900">
                      {item?.product?.label ||
                        (item?.productID
                          ? t("products.unavailable")
                          : t("invoices.table.placeholders.product"))}{" "}
                      {index + 1}
                    </h3>
                  </div>

                  {isCollapsed(index) && (
                    <div className="mt-2 ml-7 flex flex-wrap gap-2">
                      <div className="bg-gray-100 px-3 py-1 rounded-full flex items-center">
                        <span className="text-xs font-medium">{quantity}x</span>
                      </div>

                      <div className="bg-blue-50 px-3 py-1 rounded-full flex items-center">
                        <span className="text-xs font-medium text-blue-700">
                          {item?.product ? currencyFormat(itemTotal) : "-"}
                        </span>
                      </div>

                      {itemTaxes.map((tax, taxIndex) => (
                        <div
                          key={tax.id || taxIndex}
                          className={clsx(
                            "px-3 py-1 rounded-full flex items-center",
                            tax.isUnavailable
                              ? "bg-gray-100"
                              : tax.withholding
                                ? "bg-amber-50"
                                : "bg-yellow-50",
                          )}
                        >
                          <span
                            className={clsx(
                              "text-xs font-medium",
                              tax.isUnavailable
                                ? "text-gray-500 italic"
                                : tax.withholding
                                  ? "text-amber-800"
                                  : "text-yellow-700",
                            )}
                          >
                            {tax.name || t("taxes.unavailable")}
                            {tax.total > 0 &&
                              // A withheld tax is taken off the invoice, so it
                              // reads as a deduction rather than a charge.
                              ` ${tax.withholding ? "−" : ""}${currencyFormat(tax.total)}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleRemoveRowWithCollapseFix(index);
                    }}
                    className="flex items-center text-sm rounded-md p-1"
                    disabled={productItems.length === 1}
                  >
                    <TrashIcon
                      className={`h-5 w-5 text-${
                        productItems.length === 1
                          ? "gray-400 cursor-not-allowed"
                          : "red-500 cursor-pointer"
                      } `}
                    />
                  </button>
                </div>
              </div>

              {!isCollapsed(index) && (
                <div className="space-y-4 mt-4">
                  {/* Product Selection */}
                  <div>
                    <FormLabel
                      htmlFor={`product-${index}`}
                      label={t("invoices.table.headers.product")}
                      required
                    />
                    {businessProductsError ? (
                      <ErrorSelect
                        name="product"
                        customClassNames="mt-1"
                        error={businessProductsError}
                        onRefresh={onRefresh}
                      />
                    ) : (
                      <Select
                        // inputId (not id) lands on the focusable input, so the
                        // label above names the picker for assistive tech.
                        inputId={`product-${index}`}
                        className={clsx(
                          productItemsError?.[index]
                            ? "border-red-500 rounded border focus:border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                          "text-sm mt-1 w-full",
                        )}
                        classNamePrefix="select2-selection"
                        options={productOptions}
                        // The closed period guard leaves productID on the line
                        // while `product` resolves to null. An empty picker
                        // would read as "nothing chosen"; say it is there but
                        // out of reach instead.
                        value={
                          item?.product ||
                          (item?.productID
                            ? {
                                value: item.productID,
                                label: t("products.unavailable"),
                              }
                            : "")
                        }
                        onChange={(selectedOption) => {
                          // A product with no cost on file leaves the row
                          // blank on purpose: the existing "price required"
                          // check then makes the user type the real purchase
                          // price instead of inheriting a wrong number.
                          const unitPrice = selectedOption?.price;
                          const hasPrice = unitPrice > 0;
                          setProductItems(
                            updateProductItem(index, {
                              product: selectedOption,
                              price: hasPrice
                                ? { floatValue: unitPrice, value: unitPrice }
                                : { floatValue: undefined, value: "" },
                              total: {
                                floatValue: hasPrice ? unitPrice : 0,
                              },
                            }),
                          );
                        }}
                        placeholder={t("invoices.table.placeholders.product")}
                        maxMenuHeight={150}
                        styles={{
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                          }),
                        }}
                        menuPortalTarget={document.body}
                        isDisabled={productLoading}
                        isLoading={productLoading}
                      />
                    )}
                  </div>

                  {/* Quantity Input */}
                  <div>
                    <label
                      htmlFor={`quantity-${index}`}
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("invoices.table.headers.quantity")}
                    </label>
                    <input
                      type="number"
                      name="quantity"
                      id={`quantity-${index}`}
                      value={item.quantity}
                      placeholder={t("invoices.table.placeholders.quantity")}
                      className={clsx(
                        "mt-1 block w-full text-sm rounded-md shadow-sm",
                        productItemsError?.[index]?.includes("invoices.table.error.quantity")
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      )}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "") {
                          setProductItems(
                            updateProductItem(index, { quantity: "" }),
                          );
                          return;
                        }
                        const value = parseFloat(raw);
                        if (value < 0) return;
                        if (isNaN(value)) return;
                        setProductItems(
                          updateProductItem(index, {
                            quantity: value,
                          }),
                        );
                      }}
                    />
                  </div>

                  {/* Price Input */}
                  <div>
                    <label
                      htmlFor={`price-${index}`}
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("invoices.table.headers.price")}
                    </label>
                    <NumericFormat
                      type="text"
                      name="price"
                      id={`price-${index}`}
                      value={item.price?.floatValue ?? ""}
                      placeholder={t("invoices.table.placeholders.price")}
                      displayType="input"
                      thousandSeparator="."
                      decimalSeparator=","
                      prefix={"Rp"}
                      onValueChange={(values) => {
                        setProductItems(
                          updateProductItem(index, {
                            price: values,
                          }),
                        );
                      }}
                      className={clsx(
                        "mt-1 block w-full text-sm rounded-md shadow-sm",
                        productItemsError?.[index]?.includes("invoices.table.error.price")
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      )}
                    />
                  </div>

                  {/* Tax Selection */}
                  <div>
                    <label
                      htmlFor={`tax-${index}`}
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("invoices.table.headers.tax")}
                    </label>
                    <ItemTaxPicker
                      id={`tax-${index}`}
                      options={taxes}
                      value={item?.selectedTaxes}
                      isLoading={taxesLoading}
                      error={taxesError}
                      onRefresh={onRefresh}
                      onChange={(next) =>
                        setProductItems(
                          updateProductItem(index, {
                            // Only `selectedTaxes` moves. The posted terms and
                            // the line's taxID stay as the prefill left them,
                            // so clearing a tax and picking it again still
                            // charges the rate it was posted with.
                            selectedTaxes: next,
                            // The tax base belongs to whichever tax leads the
                            // line, so emptying or restarting the list drops
                            // the setting the old lead tax brought with it.
                            ...(next.length && item?.selectedTaxes?.length
                              ? {}
                              : {
                                  taxBaseType: null,
                                  taxBaseRate: null,
                                  taxBasePercentage: null,
                                  taxBaseFractionNumerator: null,
                                  taxBaseFractionDenumerator: null,
                                }),
                          }),
                        )
                      }
                    />
                  </div>

                  {/* Total */}
                  <div>
                    <label
                      htmlFor={`total-${index}`}
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("invoices.table.headers.total")}
                    </label>
                    <NumericFormat
                      type="text"
                      name="total"
                      id={`total-${index}`}
                      value={itemTotal}
                      displayType="input"
                      thousandSeparator="."
                      decimalSeparator=","
                      prefix={"Rp"}
                      disabled
                      className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 bg-gray-100"
                    />
                  </div>

                  {/* Total Tax, net of anything the counterparty withholds —
                      which can leave it negative, so the block keys on whether
                      the line has taxes at all rather than on the figure. */}
                  {itemTaxes.length > 0 && (
                    <div>
                      <div className="flex justify-between items-center">
                        <label
                          htmlFor={`tax-amount-${index}`}
                          className="text-sm font-medium text-gray-700"
                        >
                          {t("invoices.table.headers.taxTotal")}
                        </label>

                        {taxBaseRate !== 1 && (
                          <span className="text-xs text-gray-500">
                            {t("invoices.table.headers.taxBase", {
                              taxBase: currencyFormat(taxBase),
                            })}
                          </span>
                        )}
                      </div>
                      <NumericFormat
                        type="text"
                        name="tax-amount"
                        id={`tax-amount-${index}`}
                        value={itemTax}
                        displayType="input"
                        thousandSeparator="."
                        decimalSeparator=","
                        prefix={"Rp"}
                        disabled
                        className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 bg-gray-100"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Error Message */}
              {productItemsError?.[index]?.length > 0 && (
                <div className="mt-2 text-sm text-red-600 space-y-1">
                  {productItemsError[index].map((err, i) => (
                    <p key={i}>{t(err)}</p>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Add Product Button */}
        <div className="mt-4">
          <button
            type="button"
            onClick={handleAddRowWithCollapse}
            disabled={isMaxProductsReached}
            className={clsx(
              "inline-flex items-center px-4 py-2 text-sm font-medium",
              isMaxProductsReached
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-600 hover:text-blue-800",
            )}
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            {isMaxProductsReached
              ? t("invoices.table.footer.maxProductsReached", {
                  max: maxProducts,
                })
              : t("invoices.table.footer.addProduct")}
          </button>

          {isMaxProductsReached && (
            <p className="text-xs text-gray-500 mt-1">
              {t("invoices.table.footer.maxProductsMessage", {
                max: maxProducts,
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductEntryTable;
